import { useCallback, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    SysLogDTO,
    useDeleteSysLogApi,
    useSaveSysLogApi,
    useSearchSysLogApi,
} from '../../../api/sys-log-api';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveSysLogModal } from './save-sys-log-modal';

export function SysLogApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedSysLog, setSelectedSysLog] = useState<SysLogDTO | undefined>();
    const sysLogInit = useSearchSysLogApi();
    const saveSysLog = useSaveSysLogApi();
    const deleteSysLog = useDeleteSysLogApi();

    const { result, error, busy, initialized, initializing } = sysLogInit;

    const userTitle = useCallback(
        (userName?: string) => {
            var user = result?.users.find(x => x.userName === userName);
            return <span>{user ? `${user.firstName} ${user.lastName} ` : userName}</span>;
        },
        [result?.users]
    );

    const columns: IBasicTableColumn<SysLogDTO>[] = useMemo(
        () => [
            {
                Header: <T>serial</T>,
                name: 'serial',
                width: 80,
                Cell: ({ row }) => <N>{row.serial}</N>,
            },
            {
                Header: <T>type</T>,
                name: 'type',
                width: 80,
            },
            {
                Header: <T>display-message</T>,
                name: 'displayMessage',
                width: 80,
            },

            {
                Header: <T>url</T>,
                name: 'url',
                width: 80,
            },
            {
                Header: <T>query-string</T>,
                name: 'queryString',
                width: 80,
            },
            {
                Header: <T>properties</T>,
                name: 'properties',
                width: 80,
            },
            {
                Header: <T>session-id</T>,
                name: 'sessionId',
                width: 80,
                Cell: ({ row }) => <N>{row.sessionId}</N>,
            },
            {
                Header: <T>token</T>,
                name: 'token',
                width: 80,
            },
            {
                Header: <T>user-name</T>,
                name: 'userName',
                width: 80,
                Cell: ({ row }) => userTitle(row.userName),
            },
        ],
        [userTitle]
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        sysLogInit.post(null, {});
    }, [initialized, initializing, error, sysLogInit]);

    const handleDelete = (sysLog: SysLogDTO) => {
        bd.msgbox(
            translate('delete-sys-log'),
            translate('confirm-delete-sys-log'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteSysLog.remove(
                            sysLog,
                            () => {
                                sysLogInit.updateResult({
                                    ...result,
                                    sysLogs: result.sysLogs.filter(b => b.serial !== sysLog.serial),
                                });
                                notify.dark(translate('selected-sys-log-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedSysLog(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (sysLog: SysLogDTO) => {
        saveSysLog.post(
            null,
            sysLog,
            res => {
                if (selectedSysLog) {
                    sysLogInit.updateResult({
                        ...result,
                        sysLogs: result.sysLogs.map(b =>
                            b.serial === sysLog.serial ? res.sysLog : b
                        ),
                    });
                } else {
                    sysLogInit.updateResult({
                        ...result,
                        sysLogs: [res.sysLog, ...result.sysLogs],
                    });
                }

                setShowModal(false);
                setSelectedSysLog(undefined);
                notify.success(
                    selectedSysLog
                        ? translate('selected-sys-log-updated-successfully')
                        : translate('new-sys-log-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => sysLogInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar onGoBack="/home" title="sys-log" />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.sysLogs}
                        onRowClick={({ row }) => {
                            setSelectedSysLog(row);
                            setShowModal(true);
                        }}
                        hover
                    >
                        <T as="div" className="nothing-found">
                            nothing-found
                        </T>
                    </bd.BasicTable>

                    <SaveSysLogModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedSysLog(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedSysLog={selectedSysLog}
                        users={result?.users}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
